import { useRef, useState, useEffect } from 'react'
import { getConteudoChave, putConteudoChave, uploadImage, CONTEUDO_CHAVES } from '@/api/adminApi'
import { ADMIN_KEYS, CONTEUDO_API_KEYS } from '@/utils/adminStorage'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import { AdminPage, AdminPanel } from '@/components/Admin'
import adminStyles from './Admin.module.css'
import styles from './AdminQuemSomosPage.module.css'

export function AdminQuemSomosPage() {
  const fileRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')
  const [imagemUrl, setImagemUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { notifySaved, notifyError, notifyValidation } = useSaveFeedback()
  const apiChave = CONTEUDO_API_KEYS[ADMIN_KEYS.quemSomos]

  useEffect(() => {
    let cancelled = false
    setError(null)
    Promise.all([
      getConteudoChave(apiChave),
      getConteudoChave(CONTEUDO_CHAVES.quem_somos_imagem_url).catch(() => null),
    ])
      .then(([texto, img]) => {
        if (cancelled) return
        setValue(texto ?? '')
        setImagemUrl((img ?? '').trim())
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erro ao carregar')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [apiChave])

  const uploadFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      const msg = 'Escolha um ficheiro de imagem (JPG, PNG, WebP ou GIF).'
      setError(msg)
      notifyValidation(msg)
      return
    }
    setUploading(true)
    setError(null)
    try {
      const { url } = await uploadImage(file)
      setImagemUrl(url)
      notifySaved('Imagem enviada.')
    } catch (err) {
      notifyError(err, 'Erro no upload')
      setError(err instanceof Error ? err.message : 'Erro no upload')
    } finally {
      setUploading(false)
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    await uploadFile(file)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (uploading || saving) return
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    await uploadFile(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!uploading && !saving) setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await Promise.all([
        putConteudoChave(apiChave, value || null),
        putConteudoChave(CONTEUDO_CHAVES.quem_somos_imagem_url, imagemUrl || null),
      ])
      notifySaved('Quem somos guardado.')
    } catch (err) {
      notifyError(err, 'Erro ao salvar')
      setError(err instanceof Error ? err.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  const preview = imagemUrl ? resolveMediaUrl(imagemUrl) : ''
  const zoneDisabled = uploading || saving

  return (
    <AdminPage
      title="Quem somos"
      subtitle="Texto e imagem de destaque na página «Sobre nós» do app — recomendado: foto horizontal da equipa ou do espaço."
      loading={loading}
      error={error}
      wide
    >
      <form className={adminStyles.adminFormStack} onSubmit={handleSalvar}>
        <AdminPanel
          title="Imagem de destaque"
          description="Aparece no topo da página no telemóvel, com o texto por baixo. Formato largo (ex.: 1200×720) funciona melhor."
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className={adminStyles.uploadInputHidden}
            onChange={handleUpload}
          />

          <div className={styles.mediaBlock}>
            <div
              role="button"
              tabIndex={0}
              className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ''} ${zoneDisabled ? styles.dropZoneDisabled : ''}`}
              onClick={() => {
                if (!zoneDisabled) fileRef.current?.click()
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !zoneDisabled) {
                  e.preventDefault()
                  fileRef.current?.click()
                }
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {preview ? (
                <div className={styles.dropPreviewWrap}>
                  <img className={styles.dropPreviewImg} src={preview} alt="Pré-visualização" />
                  <div className={styles.dropOverlay}>
                    <span>{uploading ? 'A enviar…' : 'Trocar imagem'}</span>
                    <span className={styles.dropOverlayHint}>Clique ou largue outro ficheiro</span>
                  </div>
                </div>
              ) : (
                <div className={styles.dropInner}>
                  <span className={styles.dropIcon} aria-hidden>
                    🖼️
                  </span>
                  <p className={styles.dropTitle}>
                    {uploading ? 'A enviar imagem…' : 'Arraste uma imagem ou clique para escolher'}
                  </p>
                  <p className={styles.dropHint}>
                    JPG, PNG, WebP ou GIF · proporção larga (16:9 ou 5:3) fica melhor no telemóvel
                  </p>
                </div>
              )}
            </div>

            <details className={styles.urlBlock}>
              <summary className={styles.urlSummary}>Colar URL externa (opcional)</summary>
              <div className={styles.urlBody}>
                <label className={styles.urlLabel} htmlFor="quem-somos-img-url">
                  Endereço da imagem
                </label>
                <input
                  id="quem-somos-img-url"
                  type="url"
                  className={styles.urlInput}
                  value={imagemUrl}
                  onChange={(e) => setImagemUrl(e.target.value)}
                  placeholder="https://exemplo.com/imagem.jpg"
                  onClick={(e) => e.stopPropagation()}
                />
                <p className={styles.urlHelp}>
                  Use se a imagem já estiver noutro site. Carregar um ficheiro substitui o que estiver aqui.
                </p>
              </div>
            </details>

            {imagemUrl && (
              <div className={styles.actionsRow}>
                <button
                  type="button"
                  className={styles.btnRemoveImage}
                  onClick={() => setImagemUrl('')}
                  disabled={uploading}
                >
                  Remover imagem
                </button>
              </div>
            )}
          </div>
        </AdminPanel>

        <AdminPanel title="Texto da página" description="História, missão e valores — pode usar quebras de linha.">
          <textarea
            className={adminStyles.editorialTextarea}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Escreva aqui o texto de apresentação…"
          />
        </AdminPanel>

        <div className={adminStyles.formActions}>
          <button type="submit" className={adminStyles.btnSalvar} disabled={saving || uploading}>
            {saving ? 'A guardar…' : 'Guardar'}
          </button>
        </div>
      </form>
    </AdminPage>
  )
}
